import { useMemo } from 'react';
import { useField } from 'formik';
import { useSelector } from 'react-redux';
import { RadioFieldGroup } from '@gv/triage-components';

import { Config } from 'config';
import { RootState } from 'store';
import { isAcceptedAppointment } from 'utils/helpers';
import { useDetailsQuery } from 'store/api/hospitals';
import { Administrative } from 'components/cases/fields/actions';
import {
  CallOutcomeEmergencyTitle,
  CallOutcomeAppointmentTitle,
} from 'types/data';
import {
  CallOutcomeType,
  CallOutcomeEmergency,
  CallOutcomeAppointment,
} from 'types';
import {
  useFormEntities,
  callOutcomeModel,
  CheckProtocolsInfo,
  CorporateOptionsProps,
} from 'components/cases/form';

import { DepartmentSelect } from './department-select';

export const CallOutcomeAction = ({
  hideNotes,
  permissions,
  corporateOptions,
  isCorporateFetching,
}: CorporateOptionsProps & { hideNotes?: boolean }) => {
  const { hospital, hospitalId } = useFormEntities();
  const [type] = useField(callOutcomeModel.type);
  const { hideCallOutcomeTypes, hospitalCallOutcomeTitles } = useMemo(
    () => ({
      hideCallOutcomeTypes: Config.helpers.getHideCallOutcomeTypes(hospitalId),
      hospitalCallOutcomeTitles:
        Config.helpers.getCallOutcomeTitles(hospitalId),
    }),
    [hospitalId]
  );

  const hospitalQuery = useDetailsQuery(String(hospitalId), {
    skip: !hospitalId,
  });

  const { isShowPlanForCVT } = useSelector(
    (state: RootState) => state.analyticsExport
  );

  const isEmergency = type.value === CallOutcomeType.Emergency;
  const isAppointment = type.value === CallOutcomeType.Appointment;

  const appointmentItems = useMemo(() => {
    const appointmentOutcome =
      hospitalQuery?.data?.hospital_calloutcome?.call_outcome
        ?.filter((o) => o != null && o.outcome != null)
        ?.find((o) => o.outcome === CallOutcomeType.Appointment);

    if (!appointmentOutcome) {
      return [];
    }

    return (
      appointmentOutcome.subCallOutcome?.map((sub) => ({
        value: sub,
        label: CallOutcomeAppointmentTitle[sub as CallOutcomeAppointment],
      })) ?? []
    );
  }, [hospitalQuery?.data?.hospital_calloutcome?.call_outcome]);

  const emergencyItems = useMemo(() => {
    const emergencyOutcome =
      hospitalQuery?.data?.hospital_calloutcome?.call_outcome
        ?.filter((o) => o != null && o.outcome != null)
        ?.find((o) => o.outcome === CallOutcomeType.Emergency);

    if (!emergencyOutcome) {
      return [];
    }

    return (
      emergencyOutcome.subCallOutcome?.map((sub) => ({
        value: sub,
        label: CallOutcomeEmergencyTitle[sub as CallOutcomeEmergency],
      })) ?? []
    );
  }, [hospitalQuery?.data?.hospital_calloutcome?.call_outcome]);

  return (
    <>
      {isAppointment && (
        <div>
          <RadioFieldGroup
            label="Select option"
            name={callOutcomeModel.appointment.type}
            items={
              appointmentItems?.length > 0
                ? appointmentItems
                : permissions?.appointmentTab && isShowPlanForCVT
                  ? [
                      {
                        value: CallOutcomeAppointment.Recommended,
                        label:
                          CallOutcomeAppointmentTitle[
                            CallOutcomeAppointment.Recommended
                          ],
                      },
                      {
                        value: CallOutcomeAppointment.Scheduled,
                        label:
                          CallOutcomeAppointmentTitle[
                            CallOutcomeAppointment.Scheduled
                          ],
                        hasAccess: isAcceptedAppointment(
                          CallOutcomeAppointment.Scheduled,
                          hospital
                        ),
                      },
                      {
                        value: CallOutcomeAppointment.Cancelled,
                        label:
                          hospitalCallOutcomeTitles?.[
                            CallOutcomeAppointment.Cancelled
                          ] ??
                          CallOutcomeAppointmentTitle[
                            CallOutcomeAppointment.Cancelled
                          ],
                      },
                      {
                        value: CallOutcomeAppointment.Declined,
                        label:
                          CallOutcomeAppointmentTitle[
                            CallOutcomeAppointment.Declined
                          ],
                      },
                    ]
                  : []
            }
          />
        </div>
      )}

      {isEmergency && (
        <div>
          <RadioFieldGroup
            label="Refer to"
            name={callOutcomeModel.emergency.type}
            items={
              emergencyItems?.length > 0
                ? emergencyItems
                : [
                    {
                      value: CallOutcomeEmergency.Inbound,
                      hasAccess:
                        hospital &&
                        (hospital.speciality === 'Emergency' ||
                          hospital.speciality === 'Emergency & Specialty'),
                      label:
                        hospitalCallOutcomeTitles?.[
                          CallOutcomeEmergency.Inbound
                        ] ??
                        CallOutcomeEmergencyTitle[CallOutcomeEmergency.Inbound],
                    },
                    {
                      value: CallOutcomeEmergency.ClientDeclined,
                      hasAccess:
                        hospital &&
                        (hospital.speciality === 'Emergency' ||
                          hospital.speciality === 'Emergency & Specialty'),
                      label:
                        hospitalCallOutcomeTitles?.[
                          CallOutcomeEmergency.ClientDeclined
                        ] ??
                        CallOutcomeEmergencyTitle[
                          CallOutcomeEmergency.ClientDeclined
                        ],
                    },
                    {
                      value: CallOutcomeEmergency.ReferToOnCall,
                      label:
                        CallOutcomeEmergencyTitle[
                          CallOutcomeEmergency.ReferToOnCall
                        ],
                    },
                    {
                      hasAccess: isShowPlanForCVT,
                      value: CallOutcomeEmergency.ReferToAnotherER,
                      label:
                        hospitalCallOutcomeTitles?.[
                          CallOutcomeEmergency.ReferToAnotherER
                        ] ??
                        CallOutcomeEmergencyTitle[
                          CallOutcomeEmergency.ReferToAnotherER
                        ],
                    },
                    {
                      value: CallOutcomeEmergency.ReferToPPH,
                      label:
                        hospitalCallOutcomeTitles?.[
                          CallOutcomeEmergency.ReferToPPH
                        ] ??
                        CallOutcomeEmergencyTitle[
                          CallOutcomeEmergency.ReferToPPH
                        ],
                      hasAccess:
                        hospital &&
                        hospital.speciality !== 'Emergency' &&
                        !hideCallOutcomeTypes?.includes(
                          CallOutcomeEmergency.ReferToOnCall
                        ) &&
                        isShowPlanForCVT,
                    },
                  ]
            }
          />
        </div>
      )}

      {type.value === CallOutcomeType.Administrative && (
        <>
          <DepartmentSelect
            isDisabled={isCorporateFetching}
            options={corporateOptions?.call}
            name={callOutcomeModel.administrative.call.corporateHospitalId}
          />

          <CheckProtocolsInfo hideNotes={hideNotes}>
            <Administrative
              hospitalId={hospitalId || ''}
              name={callOutcomeModel.administrative.call.outcomes}
            />
          </CheckProtocolsInfo>
        </>
      )}
    </>
  );
};
